import { type FC, useContext } from "react";
import { isRecord } from "../../../../types/internal/utility";
import { createBorderStyles } from "../../Forms/DragDrop/BorderSettingsDialog";
import { DragDropComponentEmptyContext, DragDropComponentErrorContext } from "../../Forms/DragDrop/DragDropComponent";
import { DragDropDataContext } from "../../Forms/DragDrop/DragDropRenderer";
import { type ComponentOptions, type ComponentType } from "../../Forms/DragDrop/DragDropTypes";
import { createStyleFromPadding } from "../../Forms/PaddingInput";

export interface ReceiptTaxIndicatorValue {
    tax: string;
    text: string;
    indicator: string;
}

export interface ReceiptTaxIndicatorComponent extends ComponentType {
    type: "tax_indicator";
    value: ReceiptTaxIndicatorValue;
}

export function isTaxIndicatorComponent(component: ComponentType): component is ReceiptTaxIndicatorComponent {
    if(component.type !== "tax_indicator") {
        return false;
    }

    const { value } = component;

    return isTaxIndicatorValue(value);
}

export function isTaxIndicatorValue(value: unknown): value is ReceiptTaxIndicatorValue {
    if(typeof value !== "object") {
        return false;
    }

    if(value === null) {
        return false;
    }

    return "tax" in value && typeof value.tax === "string" &&
        "indicator" in value && typeof value.indicator === "string";
}

export const ReceiptTaxIndicatorComponent: FC<ComponentOptions> = ({ value, style }) => {
    const { indicators, tax } = useContext(DragDropDataContext);

    const error = useContext(DragDropComponentErrorContext);
    const empty = useContext(DragDropComponentEmptyContext);

    if(!isTaxIndicatorValue(value)) {
        return error;
    }

    const { border, fontSize, padding, ...styleRest } = style;

    if(!isRecord(indicators)) {
        return error;
    }

    if(!Array.isArray(tax)) {
        return error;
    }

    if(!indicators[value.tax]) {
        return empty;
    }

    let name = "No Tax";

    if(value.tax) {
        const found = tax.find((taxRate: any) => {
            return taxRate.id === value.tax;
        });

        name = (found ? found.tax : "Invalid");
    }

    return (
        <div
            className="tax-indicator"
            style={{
                fontSize: fontSize ? `${fontSize}px` : undefined,
                ...styleRest,
                ...(border ? createBorderStyles(border) : {}),
                ...(padding ? createStyleFromPadding(padding) : {}),
            }}
        >
            {value.indicator} {value.text} {name}
        </div>
    );
};
